<template>
  <div>
    <highcharts
      ref="chart"
      :constructor-type="'mapChart'"
      class="hc"
      :options="chartOptions"
      :title="`${$t('Average:')} ${averageValue}`"
    />
    <div
      v-if="compareWithGeneral && maxValue && minValue"
      class="text-center"
    >{{ $t('Average:') }} {{ averageValue }}
    </div>
  </div>
</template>

<script>
import Highcharts from 'highcharts'
import Maps from 'highcharts/modules/map'
import mapdata from '@/assets/mapdata/tr-all'
// import mapdataJson from '@/assets/mapdata/tr-all.geo.json'
import { Chart } from 'highcharts-vue'

Maps(Highcharts)

export default {
  components: {
    highcharts: Chart,
  },
  props: {
    value: {
      type: Array,
      default: null,
    },
    colorSteps: {
      type: Array,
      default: null,
    },
    compareWithGeneral: {
      type: Boolean,
      default: false,
    },
    colorBelow: {
      type: String,
      default: null,
    },
    colorAbove: {
      type: String,
      default: null,
    },
    regionMapping: {
      type: Array,
      default: null,
    },
  },
  computed: {
    isGroupedByRegion() {
      return this.regionMapping && this.regionMapping.length > 0
    },
    regionalMapData() {
      if (!this.isGroupedByRegion) {
        return mapdata
      }
      return this.createRegionalMap()
    },
    valuesHandled() {
      let data = this.value
      if (this.compareWithGeneral && data) {
        data = data.filter(x => x.name !== 'TOTAL')
      }
      return data || []
    },
    averageValue() {
      const total = this.value && this.value.find(item => item.name === 'TOTAL')
      if (!total || total.value === null || total.value === undefined || total.value === '') {
        return null
      }
      const average = Number(total.value)
      return Number.isNaN(average) ? null : average.toFixed(2)
    },
    maxValue() {
      let max = null
      if (this.value) {
        this.value.filter(v => v.value).forEach(x => {
          if (!max || ((x.value * 1) > max)) {
            max = (x.value * 1)
          }
        })
      }
      return max
    },
    minValue() {
      let min = null
      if (this.value) {
        this.value.filter(v => v.value).forEach(x => {
          if (!min || ((x.value * 1) < min)) {
            min = (x.value * 1)
          }
        })
      }
      return min
    },
    chartOptions: {
      set(value) {
        this.$emit('input', value)
      },
      get() {
        return {
          chart: {
            map: this.regionalMapData,
          },
          title: {
            text: '',
          },
          mapNavigation: {
            enabled: false,
          },
          credits: {
            enabled: false,
          },
          legend: {
            enabled: true,
          },
          tooltip: {
            headerFormat: '',
            pointFormatter() {
              return `<b>${this.name}: ${this.value}</b>${this.base ? `<br/> (n:${this.base})` : ''}`
            },
          },
          colors: this.compareWithGeneral ? [this.minValue ? this.colorBelow : '#FFFFFF', this.maxValue ? this.colorAbove : '#FFFFFF'] : this.colorSteps.map(x => x.color),
          colorAxis: {
            dataClassColor: 'category',
            dataClasses: this.compareWithGeneral ? [{ from: parseFloat(this.minValue), to: parseFloat(this.averageValue), name: this.$t('Below Average') }, { from: parseFloat(this.averageValue), to: parseFloat(this.maxValue), name: this.$t('Above Average') }] : this.colorSteps.map(x => ({ from: x.from ? parseFloat(x.from) : null, to: x.to ? parseFloat(x.to) : null, name: x.name })),
          },
          series: [{
            point: {
              events: {
                click: e => {
                  this.$emit('mapClick', e.point.name)
                },
              },
            },
            cursor: 'pointer',
            joinBy: 'name',
            name: 'Skor',
            nullColor: '#FFFFFF',
            states: {
              hover: {
                color: '#BADA55',
              },
            },
            dataLabels: {
              enabled: true,
              allowOverlap: true,
              formatter() {
                const point = this.point || this
                const val = point.value
                if (val === null || val === undefined || val === '') {
                  return null
                }
                return `<center><small>${point.name}</small><br>${val}</center>`
              },
              style: {
                fontWeight: 'normal',
                fontSize: '8px',
                color: '#fff',
                textShadow: '1px 1px 1px #666666, -1px -1px  1px #333333, -1px 1px 1px #666666, 1px -1px 1px #666666',
              },
            },
            allAreas: true,
            data: this.valuesHandled.filter(d => d.base !== '0' && d.value !== null && d.value !== undefined && d.value !== ''),
          }],
        }
      },
    },
  },
  methods: {
    createRegionalMap() {
      // Clone the original map structure
      const regionalMap = {
        ...mapdata,
        features: [],
      }

      // Group cities by region
      const regionGroups = new Map()

      mapdata.features.forEach(feature => {
        const cityName = feature.properties.name
        const region = this.getRegionForCity(cityName)

        if (!regionGroups.has(region)) {
          regionGroups.set(region, [])
        }
        regionGroups.get(region).push(feature)
      })

      // Create merged features for each region
      regionGroups.forEach((features, regionName) => {
        // Collect every polygon from the cities in this region so the merged
        // region renders as a proper MultiPolygon (one entry per city polygon).
        const allPolygons = []

        features.forEach(feature => {
          if (feature.geometry.type === 'Polygon') {
            allPolygons.push(feature.geometry.coordinates)
          } else if (feature.geometry.type === 'MultiPolygon') {
            feature.geometry.coordinates.forEach(polygon => {
              allPolygons.push(polygon)
            })
          }
        })

        // Calculate center point for the region (average of all coordinates)
        let totalLat = 0
        let totalLon = 0
        let pointCount = 0

        features.forEach(feature => {
          const lat = parseFloat(feature.properties.latitude)
          const lon = parseFloat(feature.properties.longitude)
          if (!Number.isNaN(lat) && !Number.isNaN(lon)) {
            totalLat += lat
            totalLon += lon
            pointCount += 1
          }
        })

        const avgLat = pointCount > 0 ? (totalLat / pointCount).toFixed(4) : '0'
        const avgLon = pointCount > 0 ? (totalLon / pointCount).toFixed(4) : '0'

        // Create a new feature for the region
        const regionalFeature = {
          type: 'Feature',
          id: `TR.${regionName.replace(/\s+/g, '_').toUpperCase()}`,
          properties: {
            ...features[0].properties,
            name: regionName,
            latitude: avgLat,
            longitude: avgLon,
            'hc-key': regionName.toLowerCase().replace(/\s+/g, '-'),
          },
          geometry: {
            type: 'MultiPolygon',
            coordinates: allPolygons,
          },
        }

        regionalMap.features.push(regionalFeature)
      })

      return regionalMap
    },
    getRegionForCity(cityName) {
      if (!this.regionMapping) return null
      const mapping = this.regionMapping.find(
        m => m.city && m.city.toUpperCase() === cityName.toUpperCase(),
      )
      return mapping ? mapping.region : cityName // fallback to city name if no mapping found
    },
  },
}
</script>
